<template>
  <div
    v-if="book && (toc.length || (enableThreeDContent && hasThreeDContent))"
    :class="[{ 'is-active': openToc, 'is-mobile-three-d': mobileLayout }, 'dropdown']"
  >
    <div>
      <button class="button is-primary toc-trigger"
              aria-haspopup="true"
              :aria-expanded="String(openToc)"
              :aria-label="$t('tableOfContents')"
              :class="{'toc-button-with-results': hasSearchResults}"
              @click.exact="toggleToc">
        <span class="icon">
          <i class="fas fa-lg fa-list-ol"/>
        </span>
        <span v-if="hasSearchResults" class="tag is-info">
          {{ tocResultCount }}
        </span>
      </button>
    </div>
    <vue-draggable-resizable :y="0"
                             :w="effectiveTocWidth"
                             :h="tocHeight"
                             :minw="mobileLayout ? Math.min(250, effectiveTocWidth) : 350"
                             :minh="mobileLayout ? 280 : 400"
                             :handles="['br']"
                             :active="true"
                             :draggable="false"
                             @resizing="resizing"
                             v-on-clickaway="close"
                             class="dropdown-menu">
      <div class="dropdown-content">
        <nav class="navbar is-light">
          <div v-if="activeContent === '2d'"
               class="toc-try-this-row">
            <try-this-pill
              :label="$t('trySearchingToc')"
              :local-storage-property="'trySearchingToc'"
              :tool-tip-width="'15em'"
              :tool-tip-position="'is-bottom-left'"
            />
          </div>
          <search-type-ahead
                             :placeholder="$t('searchInContent', { contentName: searchContentNameLabel })"
                             :is-bom="false"
                             :value="localQuery"
                             :content="book"
                             :show-filtered-count="showFilteredCount"
                             :total-count="tocTotalCount"
                             :total-displayed="tocTotalDisplayed"
                             :clear-on-enter="false"
                             class="toc-search"
                             @enter="search"
                             @clearClick="clearClick"
          >
            <p v-if="hasTocQuery && canNavigateResults"
               class="control">
              <button class="button is-primary is-outlined previous-button"
                      :aria-label="$t('threeD.previousResult')"
                      @click="previousResult">
                <span class="icon is-medium">
                  <i class="fa fa-chevron-left" />
                </span>
              </button>
            </p>
            <p v-if="hasTocQuery && canNavigateResults"
               class="control">
              <button class="button is-primary is-outlined next-button"
                      :aria-label="$t('threeD.nextResult')"
                      @click="nextResult">
                <span class="icon is-medium">
                  <i class="fa fa-chevron-right" />
                </span>
              </button>
            </p>
          </search-type-ahead>
          <toc-content-tabs v-if="enableThreeDContent"/>
          <div v-if="activeContent === '2d'"
               class="buttons has-addons is-centered">
            <button class="button expand-button" @click="expand">
              <span class="icon">
                <i class="fa fa-lg fa-plus-square"/>
              </span>
              <span>{{ $t('expandAll') }}</span>
            </button>
            <button class="button collapse-button" @click="collapse">
              <span class="icon">
                <i class="fa fa-lg fa-minus-square"/>
              </span>
              <span>{{ $t('collapseAll') }}</span>
            </button>
          </div>
        </nav>
        <aside v-if="activeContent === '2d'"
               class="menu is-paddingless">
          <ul v-for="item in searchFilteredTocItems"
              :key="item.id"
              class="menu-list"
              :class="{'menu-list-highlighted': isHighlighted(item)}">
            <table-of-contents-item :item="item"
                                    :item-path="getItemPath(item)"
                                    :highlighted="searchResults"
                                    :force-open="forceOpen"
                                    :is-widget="isWidget"
                                    :flat-list-idx="getFlatListIdx(getItemPath(item))"
                                    :widget-content-path="widgetContentPath"
                                    @select="select"
                                    @widgetNav="widgetNav"/>
          </ul>
        </aside>
        <toc-three-d-content
          v-else
          :before-select="mobileLayout ? closeThreeDContent : undefined"
          @select="handleThreeDContentSelected"
        />
        <div class="dropdown-footer"/>
      </div>
    </vue-draggable-resizable>
  </div>
</template>

<script>
import { computed } from 'vue';
import { mapState, mapActions, useStore, mapGetters } from 'vuex';
import bowser from 'bowser';
import {directive as onClickaway} from 'vue3-click-away';
import SearchTypeAhead from '@/components/common/search/SearchTypeAhead';
import TableOfContentsItem from './TableOfContentsItem';
import { useWidgetToc } from '@/components/widgets/composables/useWidgetToc'
import TryThisPill from "@/components/common/TryThisPill.vue";
import TocContentTabs from '@/components/library/content/TocContentTabs.vue'
import TocThreeDContent from '@/components/library/content/TocThreeDContent.vue'
import { getThreeDTocSearchResultKey } from '@/helpers/threeDTocSearch'
import { useBreakpoints } from '@/helpers'

export default {
  name: 'TableOfContents',
  props: {
    enableThreeDContent: {
      type: Boolean,
      default: false
    },
    mobileLayout: Boolean,
    query: {
      type: String,
      default: ''
    }
  },
  components: {
    TocContentTabs,
    TocThreeDContent,
    TryThisPill,
    SearchTypeAhead,
    TableOfContentsItem
  },
  directives: {
    onClickaway
  },
  data() {
    return {
      localQuery: this.query || this.$store.state.search?.filters?.tocQuery || this.searchQuery || '',
      searchResults: [],
      highlightedTocItems: [],
      scrollAfterUpdate: false,
      scrollAfterSearchUpdate: false,
      autoSelectedQuery: '',
      lastSearchQuery: String(
        this.query || this.$store.state.search?.filters?.tocQuery || ''
      ).trim(),
      bookSearch: false,
      highlightedInstance: 0,
      forceOpen: 0,
      searchRequestId: 0,
      searchPending: false,
      selectedFlatListIdx: -1
    }
  },
  setup() {
    const store = useStore()
    const { width: viewportWidth } = useBreakpoints()
    const { contentPath,
      useWidgetTocNav, getFlatListIdx } = useWidgetToc()
    const book = computed(() => store.state.content?.toc?.book)
    const isWidget = computed(() => store.getters['widgets/isWidget'])
    const searchContentNameLabel = computed(() => (book.value?.name?.length > 25)
      ? book.value?.name.substring(0,25) : book.value?.name)

    return { widgetContentPath: contentPath,
      isWidget,
      useWidgetTocNav, getFlatListIdx,
      searchContentNameLabel,
      book,
      viewportWidth
    }
  },
  computed: {
    ...mapState({
      toc: ({ content }) => content.toc.items,
      tocLoaded: ({ content }) => content.toc.isLoaded,
      isTocOpen: ({ content }) => content.toc.isTocOpen,
      tocWidth: ({ content }) => content.toc.width,
      tocHeight: ({ content }) => content.toc.height
    }),
    ...mapGetters({
      isWidget: 'widgets/isWidget',
      threeDModels: 'content/threeDModels',
      threeDSearchResults: 'content/threeDTocSearchResults'
    }),
    effectiveTocWidth () {
      if (!this.mobileLayout) return this.tocWidth

      const availableWidth = Math.max(0, Number(this.viewportWidth) - 16)
      return Math.min(Number(this.tocWidth) || 350, availableWidth)
    },
    tocQuery () {
      return this.localQuery || ''
    },
    activeContent () {
      return this.enableThreeDContent
        ? this.$store.state.content.toc.activeContent
        : '2d'
    },
    viewerActiveContent () {
      return this.$store.state.content.toc.viewerActiveContent || this.activeContent
    },
    hasThreeDContent () {
      return this.$store.state.content.toc.threeDModelSnapshots.length > 0
    },
    searchQuery() {
      return this.$route.query.exact && this.queryParam
          ? `"${this.queryParam}"`
          : this.queryParam || ''
    },
    queryParam () {
      return this.$route.query.toc
    },
    persistedTocQuery () {
      const q = this.$store.state.search?.filters?.tocQuery
      return (typeof q === 'string') ? q : ''
    },
    openToc: {
      get() {
        return this.isTocOpen;
      },
      async set(value) {
        await this.updateTocOpen(value);

        if (value) {
          this.scrollAfterUpdate = true;
        }
      }
    },
    path() {
      return this.book ? `/${this.book.contentType}/${this.book.id}/` : '';
    },
    searchFilteredTocItems () {
      // If no active query, show full TOC.
      if (!this.tocQuery?.length) return this.toc

      // If there is an active query but no results, show nothing.
      if (!this.searchResults?.length) return []

      const resultKeys = new Set(
        this.searchResults
          .filter(r => r?.entityId != null && r?.entityType)
          .map(r => `${r.entityType}:${r.entityId}`)
      )

      const filterTree = (items) => {
        return (items || []).reduce((acc, item) => {
          const key = `${item.type}:${item.id}`
          const filteredChildren = item.children ? filterTree(item.children) : undefined
          const keep = resultKeys.has(key) || (filteredChildren && filteredChildren.length > 0)

          if (keep) {
            acc.push({
              ...item,
              ...(filteredChildren ? { children: filteredChildren } : {})
            })
          }
          return acc
        }, [])
      }

      return filterTree(this.toc)
    },
    showFilteredCount() {
      return Boolean(this.localQuery?.length)
    },
    tocResultCount () {
      return Number(this.$store?.state?.content?.toc?.resultCount || 0)
    },
    hasSearchResults () {
      return this.tocResultCount > 0
    },
    tocTotalCount() {
      return this.countTocItems(this.toc || []) + (this.threeDModels || []).reduce(
        (count, model) => count + 1 + (model.snapshots?.length || 0),
        0
      )
    },
    tocTotalDisplayed() {
      if (!this.tocQuery?.length) return this.tocTotalCount
      return this.tocResultCount
    },
    hasTocQuery () {
      return this.tocQuery?.length > 0
    },
    canNavigateResults () {
      return !this.searchPending && (
        this.activeContent === '3d'
          ? (this.threeDSearchResults?.length ?? 0) > 1
          : (this.highlightedTocItems?.length ?? 0) > 1
      )
    },
    selectedThreeDSearchResultKey () {
      return this.$store.state.content.toc.selectedThreeDSearchResultKey || ''
    },
    threeDModelSnapshotsLoaded () {
      return Boolean(this.$store.state.content.toc.isThreeDModelSnapshotsLoaded)
    },
    unifiedAutoSelectionSignature () {
      return [
        this.tocQuery,
        this.searchPending,
        this.threeDModelSnapshotsLoaded,
        (this.highlightedTocItems || []).map(result => result.itemPath).join(','),
        (this.threeDSearchResults || []).map(getThreeDTocSearchResultKey).join(',')
      ].join('|')
    }
  },
  watch: {
    book (next, previous) {
      if (next?.id !== previous?.id) {
        this.autoSelectedQuery = ''
        this.lastSearchQuery = ''
      }
    },
    unifiedAutoSelectionSignature () {
      this.autoSelectUnifiedResult()
    },
    query(updated) {
      const persisted = this.$store.state.search?.filters?.tocQuery || ''
      const next = updated || persisted || ''
      if (next !== this.localQuery) {
        this.localQuery = next
        this.search(this.localQuery, true)
      }
    },
    id() {
      const width = window.innerWidth;
      if (width < 769) {
        this.openToc = false;
      }
    },
    async toc(list, previousList) {
      // For Widgets, block re-calling toc; should have it on mount
      if (this.isWidget && !!previousList.length) return

      if (this.isWidget && !this.localQuery && this.persistedTocQuery) {
        //we need to ensure that the search is retained between
        //desktop TocNavigation mobile TableOfContents
        this.localQuery = this.persistedTocQuery
      }

      await this.search(this.localQuery, true);
    },

  },
  methods: {
    ...mapActions({
      getTOCSearchResults: 'content/tocSearchWithReturn',
      navigateToContentPath: 'content/navigateToContentPath',
      updateTocOpen: 'content/updateTocOpen',
      resizeToc: 'content/tocResize',
      clearTocQuery: 'search/clearTocQuery'
    }),
    rebuildHighlightedTocItems (selectCurrent = true) {
      const results = []
      const walk = (items, parentPath) => {
        (items || []).forEach((item) => {
          const itemPath = parentPath + item.type + ':' + item.id
          if (this.isHighlighted(item)) {
            const idx = this.getFlatListIdx(itemPath)
            results.push({ itemPath, flatListIdx: idx })
          }
          const children = Array.isArray(item?.children) ? item.children : []
          if (children.length) {
            walk(children, itemPath + '/')
          }
        })
      }
      walk(this.searchFilteredTocItems || [], this.path)
      results.sort((a, b) => a.flatListIdx - b.flatListIdx)
      this.highlightedTocItems = results

      if (!selectCurrent) {
        this.selectedFlatListIdx = -1
        return
      }

      const currentPath = this.isWidget ? this.widgetContentPath : this.$route.path
      const current = results.find(r => r.itemPath === currentPath)
      this.selectedFlatListIdx = current ? current.flatListIdx : -1
    },
    nextResult () {
      if (!this.canNavigateResults) return

      if (this.activeContent === '3d') {
        this.selectAdjacentThreeDResult(1)
        return
      }

      const results = this.highlightedTocItems || []
      if (results.length < 2) return

      const currentIndex = this.viewerActiveContent === '2d'
        ? results.findIndex(r => r.flatListIdx === this.selectedFlatListIdx)
        : -1
      const nextIndex = currentIndex >= 0
        ? (currentIndex + 1) % results.length
        : 0

      const nextItem = results[nextIndex]
      if (nextItem) this.select(nextItem.itemPath)

      const active = document.activeElement
      if (active && typeof active.blur === 'function') active.blur()
    },
    previousResult () {
      if (!this.canNavigateResults) return

      if (this.activeContent === '3d') {
        this.selectAdjacentThreeDResult(-1)
        return
      }

      const results = this.highlightedTocItems || []
      if (results.length < 2) return

      const currentIndex = this.viewerActiveContent === '2d'
        ? results.findIndex(r => r.flatListIdx === this.selectedFlatListIdx)
        : -1
      const prevIndex = currentIndex >= 0
        ? (currentIndex - 1 + results.length) % results.length
        : (results.length - 1)

      const prevItem = results[prevIndex]
      if (prevItem) this.select(prevItem.itemPath)

      const active = document.activeElement
      if (active && typeof active.blur === 'function') active.blur()
    },
    async selectAdjacentThreeDResult (offset) {
      const results = this.threeDSearchResults || []
      if (results.length < 2) return

      const currentIndex = results.findIndex(result =>
        getThreeDTocSearchResultKey(result) === this.selectedThreeDSearchResultKey
      )
      const nextIndex = currentIndex >= 0
        ? (currentIndex + offset + results.length) % results.length
        : (offset > 0 ? 0 : results.length - 1)
      if (this.mobileLayout) await this.closeThreeDContent()
      await this.$store.dispatch('content/selectThreeDTocSearchResult', {
        searchResult: results[nextIndex]
      })
      if (!this.mobileLayout) await this.closeThreeDContent()

      const active = document.activeElement
      if (active && typeof active.blur === 'function') active.blur()
    },
    countTocItems(items) {
      return (items || []).reduce((acc, item) => {
        const children = Array.isArray(item?.children) ? item.children : []
        const type = (item?.type == null ? '' : String(item.type)).trim().toLowerCase()
        const isPage = type === 'page'
        return acc + (isPage ? 1 : 0) + this.countTocItems(children)
      }, 0)
    },
    isHighlighted(item) {
      //avoid type comparison errors by normalizing the data
      const normalizeId = (v) => (v == null ? '' : String(v))
      const normalizeType = (v) => (v == null ? '' : String(v).trim().toLowerCase())

      const itemId = normalizeId(item?.id)
      const itemType = normalizeType(item?.type)

      return (this.searchResults || []).some((tocItem) => {
        return normalizeId(tocItem?.entityId) === itemId &&
          normalizeType(tocItem?.entityType) === itemType
      })
    },
    async search(query, bookSearch) {
      const requestId = ++this.searchRequestId
      const q = (typeof query === 'string') ? query : ''
      const normalizedQuery = q.trim()
      const queryChanged = normalizedQuery !== this.lastSearchQuery
      if (this.autoSelectedQuery !== normalizedQuery) this.autoSelectedQuery = ''
      this.searchPending = true
      this.localQuery = q
      if (queryChanged) {
        this.searchResults = []
        this.highlightedTocItems = []
        this.selectedFlatListIdx = -1
        this.highlightedInstance = -1
        this.clearActiveTocHighlighted()
      }
      if (q) {
        await this.$store.dispatch('search/setTocQuery', q)
      } else {
        await this.$store.dispatch('search/clearTocQuery')
      }
      if (q) {
        const results = await this.getTOCSearchResults({query: q});
        if (requestId !== this.searchRequestId) return
        this.searchPending = false
        this.searchResults = results
        // Do not auto-scroll or auto-select a match after searching.
        this.highlightedInstance = -1
        this.clearActiveTocHighlighted()
        this.lastSearchQuery = normalizedQuery
        this.rebuildHighlightedTocItems(!queryChanged)
        const autoSelected = await this.autoSelectUnifiedResult()
        if (this.tocResultCount > 0 && !autoSelected) {
          this.openToc = true;
          this.bookSearch = bookSearch || false;
        }
      } else {
        await this.getTOCSearchResults({ query: '' })
        if (requestId !== this.searchRequestId) return
        this.lastSearchQuery = ''
        this.searchPending = false
        this.searchResults = []
        this.highlightedInstance = -1
        this.selectedFlatListIdx = -1
        this.highlightedTocItems = []
        this.clearActiveTocHighlighted()
      }
    },
    async autoSelectUnifiedResult () {
      const query = this.tocQuery.trim()
      if (!query || this.searchPending || !this.threeDModelSnapshotsLoaded) return false
      if (this.autoSelectedQuery === query) return false

      const unifiedResultCount = (this.highlightedTocItems?.length ?? 0) +
        (this.threeDSearchResults?.length ?? 0)
      if (unifiedResultCount !== 1) return false

      this.autoSelectedQuery = query
      if (this.highlightedTocItems.length === 1) {
        const only = this.highlightedTocItems[0]
        if (only?.itemPath) {
          await this.select(only.itemPath)
          await this.$store.dispatch('search/setBomQuery', this.tocQuery)
          return true
        }
        return false
      }

      if (this.mobileLayout) await this.closeThreeDContent()
      const selected = await this.$store.dispatch('content/selectThreeDTocSearchResult', {
        searchResult: this.threeDSearchResults[0]
      })
      if (selected && !this.mobileLayout) await this.closeThreeDContent()
      return Boolean(selected)
    },
    async handleThreeDContentSelected () {
      if (!this.mobileLayout) await this.closeThreeDContent()
    },
    toggleToc() {
      this.openToc = !this.openToc;
    },
    /**
     * Closes the TOC dropdown.
     *
     * Triggered by the click-away directive on the
     * vue-draggable-resizable component. To avoid closing
     * the TOC when we actually want to open it, check to
     * see if the click event target contains the toc-trigger
     * class. If it does, do nothing, it doesn't close the TOC.
     *
     * @param {object} event click event info
     */
    close(event) {
      if (bowser.check({msie: '11'}, true)) {
        if (event.target.msMatchesSelector('.toc-trigger, .toc-trigger *')) return;
      } else {
        if (event.target.matches('.toc-trigger, .toc-trigger *')) return;
      }

      if (this.openToc) {
        this.openToc = false;
      }
    },
    async select(itemPath) {
      await this.$store.dispatch('content/updateViewerActiveContent', '2d')
      // Close the mobile TOC dropdown before navigating so the user sees
      // immediate feedback and the next page isn't shown under an open TOC.
      if (this.openToc) {
        this.openToc = false
        await this.$nextTick()
      }

      if (this.tocQuery?.length) {
        const idx = this.getFlatListIdx(itemPath)
        this.selectedFlatListIdx = idx
      }
      const q = (typeof this.localQuery === 'string') ? this.localQuery : ''

      if (this.isWidget && q) {
        await this.$store.dispatch('search/setBomQuery', q)
      }
      if (q && this.persistedTocQuery !== q) {
        await this.$store.dispatch('search/setTocQuery', q)
      }

      if (this.isWidget) {
        await this.widgetNav(itemPath)
        return
      }

      await this.navigateToContentPath({
        path: itemPath
      });
    },
    async closeThreeDContent () {
      if (!this.openToc) return

      this.openToc = false
      await this.$nextTick()
    },
    async widgetNav(itemPath) {
      const q = (typeof this.localQuery === 'string') ? this.localQuery : ''
      if (q) {
        await this.$store.dispatch('search/setBomQuery', q)
      }
      return this.useWidgetTocNav(itemPath)
    },
    scrollTo(id) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView();
      }
    },
    clearActiveTocHighlighted() {
      const active = document.getElementsByClassName('menu-list-highlighted-active')
      while ((active?.length ?? 0) > 0) {
        active.item(0)?.classList.remove('menu-list-highlighted-active')
      }
    },
    expand() {
      this.forceOpen += 1;
    },
    collapse() {
      this.forceOpen -= 1;
    },
    async resizing(left, top, width, height) {
      await this.resizeToc({ width, height});
    },
    getItemPath(item) {
      return this.path + item.type + ':' + item.id
    },
    clearClick () {
      this.searchRequestId++
      this.autoSelectedQuery = ''
      this.lastSearchQuery = ''
      this.searchPending = false
      this.localQuery = ''
      this.searchResults = []
      this.highlightedInstance = -1
      this.selectedFlatListIdx = -1
      this.highlightedTocItems = []
      this.clearActiveTocHighlighted()
      this.clearTocQuery()
      this.getTOCSearchResults({ query: '' })
    }
  },
  mounted() {
    if (this.tocLoaded) {
      this.search(this.localQuery, true);
    }
    // Ensure Widgets always open ToC irrespective of tenant or user settings
    if (this.isWidget) {
      this.openToc = true
      const q = this.persistedTocQuery
      if (q && this.localQuery !== q) this.localQuery = q
      if (this.localQuery) this.search(this.localQuery, true)
    }
  },
  async updated() {
    if (this.scrollAfterUpdate) {
      this.scrollTo(this.id);
      this.scrollAfterUpdate = false;
    }
  },
  unmounted() {}
};
</script>

<style scoped>
.dropdown {
  flex-shrink: 0;
}
.dropdown.is-active .dropdown-menu, .dropdown.is-hoverable:hover .dropdown-menu {
  display: flex;
}
.menu {
  padding: .5rem;
  flex-grow: 2;
  overflow: auto;
}
.dropdown-menu {
  box-shadow: 15px 30px 80px 0 lightgrey;
  z-index: 25 !important;
  max-width: 80vw;
  max-height: 80vh;
}
.dropdown-content {
  padding-top:  0;
  padding-bottom: 0;
  display: flex !important;
  flex-direction: column;
  flex-grow: 2;
  overflow: hidden;
  min-height: 300px;
  min-width: 250px;
  height: 100%;
  z-index: 25;
}
.menu-list {
  border-left: .25rem solid #dbdbdb;
  margin: 0 0 0 .1rem;
  padding: 0 0 0 .1rem;
}
.menu-list-highlighted {
  border-left-color: #209CEE;
}
.navbar {
  flex-direction: column;
  padding: .5rem;
  flex-shrink: 0;
  border-bottom: lightgray 1px solid;
}
.toc-search {
  padding: 0;
  margin-bottom: .5rem;
  position: relative;
  z-index: 3000;
}

.toc-search :deep(.menu-list) {
  z-index: 4000;
}
.is-outlined {
  background-color: white !important;
  color: grey !important;
}
.toc-button-with-results {
  padding-right: .25rem;
}

.toc-trigger .tag {
  margin-left: 0.25em;
}
.buttons > .button {
  width: 50%;
}
.dropdown-footer {
  border-top: lightgray 1px solid;
  background-color: #f5f5f5;
  border-radius: 3px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  margin-right: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.dropdown.is-mobile-three-d .dropdown-menu {
  max-height: calc(100vh - 1rem);
  max-height: calc(100dvh - 1rem);
  max-width: calc(100vw - 1rem);
}

.dropdown.is-mobile-three-d .navbar {
  min-width: 0;
}

.dropdown.is-mobile-three-d .dropdown-content {
  min-width: 0;
}

.dropdown.is-mobile-three-d .toc-search :deep(.control.is-expanded) {
  min-width: 0;
}

.dropdown.is-mobile-three-d .toc-search :deep(.button) {
  min-height: 2.75rem;
  min-width: 2.75rem;
}

.dropdown.is-mobile-three-d :deep(.toc-content-tabs .button),
.dropdown.is-mobile-three-d :deep([data-unit="toc-3d-content"] .toc-row) {
  min-height: 2.75rem;
}

.dropdown.is-mobile-three-d :deep([data-unit="toc-3d-content"] .toc-item-text) {
  max-width: 100%;
  min-width: 0;
}

.dropdown.is-mobile-three-d :deep([data-unit="toc-3d-content"] .toc-item-text > span:first-child) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media only screen and (max-width: 768px) {
  .dropdown-menu {
    max-width: 95vw;
    max-height: 80vh;
  }
  .dropdown-footer {
    margin-right: 0;
  }
}

@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  /* no-op */
}

.toc-try-this-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem 0.75rem 0.75rem;
  margin-bottom: 0.25rem;
}

.toc-try-this-row :deep(.tooltip .tooltiptext.is-bottom),
.toc-try-this-row :deep(.tooltip .tooltiptext.is-top) {
  transform: translateX(-65%);
}
</style>
